import React, { createContext, FC, PropsWithChildren, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
    type IVariationThumbnailRenderer,
    type MultiPageImageModel,
    type MultiPageVideoModel,
} from '@bynder-studio/render-core';
import useVariations from 'packages/pages/design/hooks/useVariations';
import useDesign from 'packages/pages/design/hooks/useDesign';
import { applyVariationDataToCreativeModel } from 'packages/pages/design/sidebar/variations/utils';
import { variationsThumbnailsStore } from 'packages/pages/design/VariationsContext/variationsThumbnailsStore';
import {
    Variation,
    VariationsThumbnailsContext,
    VariationsThumbnailsContextValue,
} from 'packages/pages/design/VariationsContext/types';
import { colorPalettes } from 'packages/store/brandColors/brandColors.selectors';
import { syncBrandColors } from 'packages/pages/editor/syncBrandColors';
import { BrandColorPalette } from 'packages/store/brandColors/types';
import useAccessRights from 'packages/hooks/useAccessRights';
import features from 'packages/configs/features';

export { VariationsThumbnailsContext, VariationsThumbnailsContextValue };

/**
 * syncBrandColors only operates on the "current page" of a MultiPageModel.
 * This helper iterates all pages, syncing brand colors on each one,
 * then restores the original page index.
 */
function syncBrandColorsAllPages(model: MultiPageImageModel | MultiPageVideoModel, palettes: BrandColorPalette[]) {
    const originalPageIndex = model.getCurrentPageIndex();
    const pageCount = model.getModels().length;

    for (let i = 0; i < pageCount; i++) {
        model.setCurrentPageIndex(i);
        syncBrandColors(model, palettes, () => {});
    }

    model.setCurrentPageIndex(originalPageIndex);
}

export type VariationsThumbnailsProviderProps = PropsWithChildren;

export const VariationsThumbnailsProvider: FC<VariationsThumbnailsProviderProps> = ({ children }) => {
    const { creativeModelFactory, creativeModel } = useDesign<MultiPageImageModel | MultiPageVideoModel>();
    const { isPlatformAllowed } = useAccessRights();
    const isLiveBrandColorsDisabled = isPlatformAllowed([features.LIVE_BRAND_COLORS_DISABLED]);
    const { variations = [] } = useVariations();
    const { palettes } = useSelector(colorPalettes);
    const palettesRef = useRef(palettes);
    const idKeys = variations
        .map((variation) => variation.id)
        .sort()
        .join('_');
    const variationThumbnailRenderer = useMemo(
        () => creativeModelFactory.getVariationThumbnailRenderer(),
        [creativeModelFactory],
    );
    const pageIndex = creativeModel.getCurrentPageIndex();

    const swapVariationThumbnailModel = useCallback(
        (variation: { oldId: string; id: string }) => {
            const variationIdsSet = variationThumbnailRenderer.getVariationIds();
            const model = creativeModel.getCopy();

            applyVariationDataToCreativeModel(variation, model, { skipGlobalAudio: true });

            variationIdsSet.delete(variation.oldId);
            variationThumbnailRenderer.removeModel(variation.oldId);
            variationThumbnailRenderer.addModel(variation.id, model);

            if (!isLiveBrandColorsDisabled) {
                syncBrandColorsAllPages(model as MultiPageImageModel | MultiPageVideoModel, palettes);
            }

            variationsThumbnailsStore.deleteVariationThumbnails(variation.oldId);
        },
        [variationThumbnailRenderer, creativeModel, palettes, isLiveBrandColorsDisabled],
    );

    useEffect(() => {
        variationsThumbnailsStore.setDefaultThumbnailSize(variationThumbnailRenderer.getThumbnailSize());
        const unsubscribes = [
            variationThumbnailRenderer.onRenderStart(variationsThumbnailsStore.variationsThumbnailRenderStart),
            variationThumbnailRenderer.onRenderEnd(variationsThumbnailsStore.variationsThumbnailRenderEnd),
            variationThumbnailRenderer.onPageChanged(variationsThumbnailsStore.pageChanged),
        ];

        return () => {
            unsubscribes.forEach((unsubscribe) => unsubscribe());
        };
    }, [variationThumbnailRenderer]);

    // on variations change
    useEffect(() => {
        const variationIdsSet = variationThumbnailRenderer.getVariationIds();
        variations.forEach((variation) => {
            if (variationIdsSet.has(variation.oldId) || variationIdsSet.has(variation.id)) {
                variationIdsSet.delete(variation.id);

                if (variation.oldId && variationIdsSet.has(variation.oldId)) {
                    variationIdsSet.delete(variation.oldId);
                    variationThumbnailRenderer.swapModel(variation.oldId, variation.id);
                    variationsThumbnailsStore.swapVariationThumbnails(variation.oldId, variation.id);
                }

                return;
            }

            const model = creativeModel.getCopy();
            applyVariationDataToCreativeModel(variation, model, { skipGlobalAudio: true });
            variationThumbnailRenderer.addModel(variation.id, model);

            if (!isLiveBrandColorsDisabled) {
                syncBrandColorsAllPages(model as MultiPageImageModel | MultiPageVideoModel, palettes);
            }
        });
        variationIdsSet.forEach((variationId) => {
            variationThumbnailRenderer.removeModel(variationId);
            variationsThumbnailsStore.deleteVariationThumbnails(variationId);
        });
    }, [
        variationThumbnailRenderer,
        creativeModel,
        idKeys,
        variations,
        variations.length,
        palettes,
        isLiveBrandColorsDisabled,
    ]);

    // sync brand colors on existing per-variation thumbnail models when palettes change
    useEffect(() => {
        if (palettesRef.current === palettes) {
            palettesRef.current = palettes;

            return;
        }

        palettesRef.current = palettes;

        variationThumbnailRenderer.getVariationIds().forEach((variationId) => {
            const model = variationThumbnailRenderer.getMultiPageModel(variationId);

            if (model && !isLiveBrandColorsDisabled) {
                syncBrandColorsAllPages(model as MultiPageImageModel | MultiPageVideoModel, palettes);
            }
        });
    }, [palettes, variationThumbnailRenderer, isLiveBrandColorsDisabled]);

    // on page change
    useEffect(() => {
        variationThumbnailRenderer.setCurrentPage(pageIndex);
        variationThumbnailRenderer.getVariationIds().forEach((variationId) => {
            if (variationsThumbnailsStore.isThumbnailsReady(variationId, pageIndex)) {
                return;
            }

            variationThumbnailRenderer.render(variationId, pageIndex);
        });
    }, [variationThumbnailRenderer, pageIndex]);

    const value = useMemo(
        () => ({ variationThumbnailRenderer, swapVariationThumbnailModel }),
        [variationThumbnailRenderer, swapVariationThumbnailModel],
    );

    // @ts-ignore
    return <VariationsThumbnailsContext.Provider value={value}>{children}</VariationsThumbnailsContext.Provider>;
};
